import { Button, IconName } from '@billy/design-system'
import React, { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useBankTransactionsAccess } from '@features/bankTransactions/hooks/useBankTransactionsAccess'
import { useSyncOpenBankingConnection } from '@features/openBanking/hooks/useSyncOpenBankingConnection'
import { useUserOrganization, useUserOrganizationSettings } from '@modules-deprecated/app/organization'
import { SubscriptionPlan } from '@modules-deprecated/app/organization/enums/subscriptionPlan'

import { EmberRoute } from '../../../../../../enums/emberRoute'
import { useLocalStorage } from '../../../../../../hooks'
import { useEmberRouteUrl } from '../../../../../../hooks/routing/useEmberRouteUrl'
import { useOrganizationViewUrl } from '../../../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../../../utils/routing/reactRoute'
import { widgetId } from '../../../../constants/widgetId'
import { useWidgetsActions } from '../../../../hooks/useWidgetsActions'
import { BankIntegrationActionPoint } from '../../enums/bankIntegrationActionPoint'

type ButtonActionType = 'error' | 'onPremiumPlan' | 'onBasicPlan' | 'inboxEnabled'

interface ButtonAction {
  tLabel: string
  action: () => void
  icon?: IconName
}

export interface ConnectionCTAProps {
  accountId?: string | null
  bankAccountId?: string | null
  isError: boolean
}

export const ConnectionCTA = ({ accountId, bankAccountId, isError }: ConnectionCTAProps): ReactElement => {
  const { t } = useTranslation()
  const { isLoading, syncOpenBankingAccountConnection } = useSyncOpenBankingConnection()
  const { organization } = useUserOrganization()
  const { organizationSettings } = useUserOrganizationSettings()
  const navigate = useNavigate()
  const [bankReconciliationFlag] = useLocalStorage<number>('labs-flag:reactBankReconciliation', 0)
  const { isTransactionsDefaultView } = useBankTransactionsAccess()
  const { url: bankReconciliationUrl } = useEmberRouteUrl(EmberRoute.Bank, accountId)
  const { url: bankReconciliationDeprecatedUrl } = useEmberRouteUrl(EmberRoute.BankReact, accountId)
  const { url: settingsUrl } = useEmberRouteUrl(EmberRoute.SettingsSubscription)
  const { url: thingsToDoUrl } = useOrganizationViewUrl(reactRoute.organization.thingsToDo.getMainRoute())
  const { url: bankTransactionsUrl } = useOrganizationViewUrl(
    reactRoute.organization.bank.getBankTransactionsRoute(),
    accountId ? { accountId } : undefined,
  )
  const { trackDashboardActionPerformed } = useWidgetsActions()

  const isInboxEnabled = accountId && organizationSettings?.isInboxEnabled === '1'
  const isPremiumPlan = accountId && organization?.subscriptionPlan === SubscriptionPlan.Premium

  const ctaButtonActions: Record<ButtonActionType, ButtonAction> = useMemo(
    () => ({
      error: {
        tLabel: 'dashboard.widget.bank-integration.renew_connection',
        action: () => {
          trackDashboardActionPerformed({
            action: BankIntegrationActionPoint.RenewConnection,
            widgetId: widgetId.BankIntegration,
          })

          if (bankAccountId) {
            syncOpenBankingAccountConnection(bankAccountId)
          }
        },
        icon: 'refresh',
      },
      inboxEnabled: {
        tLabel: 'dashboard.widget.bank-integration.go_to_things_to_do',
        action: () => {
          trackDashboardActionPerformed({
            action: BankIntegrationActionPoint.ThingsToDo,
            widgetId: widgetId.BankIntegration,
          })

          if (thingsToDoUrl) {
            navigate(thingsToDoUrl)
          }
        },
      },
      onPremiumPlan: {
        tLabel: isTransactionsDefaultView
          ? 'dashboard.widget.bank-integration.go_to_bank_transactions'
          : 'dashboard.widget.bank-integration.go_to_bank_recon',
        action: () => {
          const oldBankReconciliationUrl = bankReconciliationFlag
            ? bankReconciliationDeprecatedUrl
            : bankReconciliationUrl
          const bankReconciliationPageUrl = isTransactionsDefaultView ? bankTransactionsUrl : oldBankReconciliationUrl

          trackDashboardActionPerformed({
            action: BankIntegrationActionPoint.GoToBankReconciliation,
            widgetId: widgetId.BankIntegration,
          })

          if (accountId && bankReconciliationPageUrl) {
            navigate(bankReconciliationPageUrl)
          }
        },
      },
      onBasicPlan: {
        tLabel: 'dashboard.widget.bank-integration.upgrade_to_premium',
        action: () => {
          trackDashboardActionPerformed({
            action: BankIntegrationActionPoint.UpgradeToPremium,
            widgetId: widgetId.BankIntegration,
          })

          if (settingsUrl) {
            navigate(settingsUrl)
          }
        },
      },
    }),
    [
      accountId,
      bankAccountId,
      bankReconciliationDeprecatedUrl,
      bankReconciliationFlag,
      bankReconciliationUrl,
      bankTransactionsUrl,
      isTransactionsDefaultView,
      navigate,
      settingsUrl,
      syncOpenBankingAccountConnection,
      thingsToDoUrl,
      trackDashboardActionPerformed,
    ],
  )

  const buttonAction = useMemo((): ButtonAction => {
    if (isError) {
      return ctaButtonActions.error
    }

    if (isInboxEnabled) {
      return ctaButtonActions.inboxEnabled
    }

    if (isPremiumPlan) {
      return ctaButtonActions.onPremiumPlan
    }

    return ctaButtonActions.onBasicPlan
  }, [isError, isInboxEnabled, isPremiumPlan, ctaButtonActions])

  return (
    <>
      {organization && (
        <Button
          icon={buttonAction?.icon}
          loading={isLoading}
          onClick={buttonAction.action}
          variant={isError || !isPremiumPlan ? 'filled' : 'tonal'}
        >
          {t(buttonAction.tLabel)}
        </Button>
      )}
    </>
  )
}
